import React, { useState, useContext, useCallback } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { NetworkStatus } from 'apollo-client';

import type { UIAnalyticsEventHandler, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AnalyticsListener } from '@atlaskit/analytics-next';
import { Box, xcss, Flex } from '@atlaskit/primitives';

import type { BannerStateContainer } from '@confluence/banners';
import { ContentTypesHeader } from '@confluence/content-types-header';
import type { ContentTypeShellProps } from '@confluence/content-types-bridge';
import { ContentTypesHeaderPlaceholder } from '@confluence/content-types-header-base';
import { DATABASE_EXPERIENCE, ExperienceTrackerContext } from '@confluence/experience-tracker';
import { ContentViewedEvent } from '@confluence/content-body';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { DATABASE_VIEW } from '@confluence/named-routes';
import { NotFoundContained } from '@confluence/content-prerequisites';
import { useSessionData } from '@confluence/session-data';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import { ArchivedPageBanner } from '@confluence/archive-pages';
import {
	WhiteboardResolvedCommentProvider,
	useIsVersionHistoryEnabled,
	useResizeBanner,
} from '@confluence/content-types-utils';
import { useIsSidePanelOpen } from '@confluence/side-panel-state-container';
import { VersionHistory } from '@confluence/version-history';
import { ContentTypeEventService } from '@confluence/content-types-bridge/entry-points/content-types-event-service';

import { DatabaseResolvedCommentsService } from './services/database-resolved-comment-service';
import type { ResolvedDatabaseCommentIds } from './services/database-resolved-comment-service';
import { DatabaseOnboarding } from './DatabaseOnboarding';
import { MobileWebView } from './components/DatabaseMobileView';
import { DATABASE_VIEW_PAGE_CONTENT_SEGMENT, StartDatabasePageLoad } from './perf.config';
import { databaseHeightContainer } from './database-shell-styles';
import type { DatabaseIdentity } from './database-shell-types';
import { useDatabaseHeaderActions, useOnboardingService } from './hooks';
import { useDatabaseParams } from './database-params';
import { DatabaseIframe } from './components/DatabaseIframe';

const databaseShellContainerStyles = xcss({
	height: '100%',
	width: '100%',
	position: 'relative',
	overflow: 'hidden',
});

const contentHeaderWrapperStyles = xcss({
	borderBottomWidth: 'border.width',
	borderBottomStyle: 'solid',
	borderBottomColor: 'color.border',
	backgroundColor: 'elevation.surface',
});

const archivedBannerContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	width: '100%',
});

const archivedBannerStyles = xcss({
	marginTop: 'space.200',
});

type DatabaseShellProps = ContentTypeShellProps & {
	spaceId?: string | null;
	spaceName?: string | null;
	title?: string | null;
	identity?: DatabaseIdentity;
	dataSecurityPolicyPageExport: boolean | null;
};

export const DatabaseShell = ({
	spaceKey,
	contentId,
	contentReferenceId,
	loading,
	queryNetworkStatus,
	spaceId,
	spaceName,
	title,
	identity,
	refetch,
	dataSecurityPolicyPageExport,
}: DatabaseShellProps) => {
	const isSSR = Boolean(process.env.REACT_SSR);
	const isLoadingNewContent = loading && queryNetworkStatus !== NetworkStatus.refetch;

	const { bannerHeight: archivedBannerHeight, setBannerElement: setArchivedBannerHeight } =
		useResizeBanner(isSSR);

	const {
		bannerHeight: databaseHeaderHeight,
		setBannerElement: setDatabaseHeaderElement,
		setBannerHeight: setDatabaseHeaderHeight,
	} = useResizeBanner(isSSR);

	const isMobile = shouldShowMobileWeb();
	const contentActions = useDatabaseHeaderActions();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const onboardingService = useOnboardingService();
	const { userId } = useSessionData();
	const [resolvedCommentService] = useState(() => new DatabaseResolvedCommentsService());
	const [eventService] = useState(() => new ContentTypeEventService());

	const params = useDatabaseParams();
	const isSmartLinkEmbed = params['isSmartLinkEmbed'];
	const isVersionHistoryEnabled = useIsVersionHistoryEnabled('database');

	const isSidePanelOpen = useIsSidePanelOpen();

	const reopenResolvedCommentCallback = useCallback(
		(commentIds: ResolvedDatabaseCommentIds) => {
			resolvedCommentService.onReopenResolvedComment(commentIds);
		},
		[resolvedCommentService],
	);

	const updateDatabaseHeaderEvents: UIAnalyticsEventHandler = useCallback(
		(event: UIAnalyticsEvent) => {
			const newData = {
				objectType: 'database',
			};

			const newAttributes = {
				contentType: 'database',
			};

			event.update((payload) => {
				const mergedAttributes = {
					...payload.data?.attributes,
					...newAttributes,
				};
				return {
					...payload,
					data: {
						...payload.data,
						...newData,
						attributes: mergedAttributes,
					},
				};
			});
		},
		[],
	);

	const showContentTypesHeader =
		!isSSR && !isLoadingNewContent && !params.isSmartLinkEmbed && !!userId;

	const showContentTypesHeaderPlaceholder =
		isLoadingNewContent && !params.isSmartLinkEmbed && !!userId;

	if (!loading && !contentReferenceId) {
		experienceTracker.fail({
			name: DATABASE_EXPERIENCE,
			error: new Error('DatabaseUnifiedQuery did not return valid referenceId'),
		});
		return <NotFoundContained route={DATABASE_VIEW} spaceKey={spaceKey} />;
	}

	return (
		<>
			<DatabaseOnboarding onboardingService={onboardingService} />
			<StartDatabasePageLoad contentId={contentId} />
			<Subscribe to={databaseHeightContainer}>
				{(bannerState: BannerStateContainer) => (
					<Flex direction="column" xcss={databaseShellContainerStyles}>
						{showContentTypesHeaderPlaceholder && <ContentTypesHeaderPlaceholder />}
						{showContentTypesHeader && (
							<AnalyticsListener onEvent={updateDatabaseHeaderEvents}>
								<WhiteboardResolvedCommentProvider callback={reopenResolvedCommentCallback}>
									{isVersionHistoryEnabled ? (
										<Box
											testId="pinned-ct-header"
											xcss={contentHeaderWrapperStyles}
											ref={setDatabaseHeaderElement}
										>
											<ContentTypesHeader contentId={contentId} contentActions={contentActions} />
										</Box>
									) : (
										<ContentTypesHeader
											contentId={contentId}
											contentActions={contentActions}
											onHeightChange={setDatabaseHeaderHeight}
										/>
									)}
								</WhiteboardResolvedCommentProvider>
							</AnalyticsListener>
						)}
						{!isSmartLinkEmbed && (
							<Box xcss={archivedBannerContainerStyles} ref={setArchivedBannerHeight}>
								<Box xcss={archivedBannerStyles}>
									<ArchivedPageBanner contentId={contentId} marginStyle="none" />
								</Box>
							</Box>
						)}
						{isMobile ? (
							<MobileWebView bannerState={bannerState} />
						) : (
							<>
								<PageSegmentLoadStart metric={DATABASE_VIEW_PAGE_CONTENT_SEGMENT} />
								<DatabaseIframe
									contentId={contentId}
									contentReferenceId={contentReferenceId}
									spaceKey={spaceKey}
									dataSecurityPolicyPageExport={dataSecurityPolicyPageExport}
									identity={identity}
									refetch={refetch}
									spaceId={spaceId}
									spaceName={spaceName}
									title={title}
									loading={loading}
									isSmartLinkEmbed={isSmartLinkEmbed}
									headerHeight={databaseHeaderHeight + archivedBannerHeight}
									bannerState={bannerState}
									resolvedCommentService={resolvedCommentService}
									isSidePanelOpen={isSidePanelOpen}
								/>
								{isSidePanelOpen && (
									<VersionHistory
										headerHeight={bannerState.getTotalHeight(true) + databaseHeaderHeight}
										contentId={contentId}
										panelTop={databaseHeaderHeight}
										eventService={eventService}
										setVersion={() => {}} // TODO: will be addressed in CTIA-2914
										contentType="database"
									/>
								)}
								{!isSSR && <ContentViewedEvent contentId={contentId} />}
							</>
						)}
					</Flex>
				)}
			</Subscribe>
		</>
	);
};
